import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Loader2, Upload } from 'lucide-react';

/**
 * /drop/:token — where files reach Hey Joe from a Claude chat (fileDrops.js).
 * A connector tool made this link for one event or store. Each slot takes its
 * files; images for the event cover, host photo, store photo and banner land in
 * place the moment they finish. No sign-in: the link is the key, for 7 days.
 */
type Slot = { slot: string; label: string; accept: 'image' | 'video' | 'any'; multiple: boolean; current: string | null };
type DropFile = { slot: string; name: string; type: string; size: number; url: string };
type Drop = { title: string; note: string | null; expired: boolean; expiresAt: string; slots: Slot[]; files: DropFile[] };
type Progress = { id: string; name: string; pct: number; done?: string; error?: string };

const HINT: Record<string, string> = {
  event_cover: 'A wide image works best (16:9). It shows on the event page and in emails.',
  host_photo: 'A headshot. It shows next to the host’s name on the event page.',
  store_avatar: 'A headshot or logo, shown round on the store.',
  store_banner: 'A wide image across the top of the store.',
  intro_video: 'A short pitch for the class, shown on its page before people buy. MP4 or MOV, up to 1 GB; it plays a minute or two after it lands.',
  files: 'Anything else: PDFs, sides, scripts, videos, more images.',
};

function put(url: string, file: File, onPct: (n: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('PUT', url);
    x.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    x.upload.onprogress = (e) => { if (e.lengthComputable) onPct(Math.round((e.loaded / e.total) * 100)); };
    x.onload = () => (x.status >= 200 && x.status < 300 ? resolve() : reject(new Error(`Upload failed (${x.status})`)));
    x.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
    x.send(file);
  });
}

const DropPage: React.FC = () => {
  const { token } = useParams();
  const [drop, setDrop] = useState<Drop | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, Progress[]>>({});

  const load = useCallback(async () => {
    const r = await fetch(`/api/file-drops/${token}`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(j.error || 'This link is not valid.'); return; }
    setDrop(j as Drop);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const send = async (slot: Slot, files: FileList | File[]) => {
    const list = Array.from(files).slice(0, slot.multiple ? 20 : 1);
    for (const file of list) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setProgress(p => ({ ...p, [slot.slot]: [...(p[slot.slot] || []), { id, name: file.name, pct: 0 }] }));
      const update = (patch: Partial<Progress>) => setProgress(p => ({ ...p, [slot.slot]: (p[slot.slot] || []).map(x => (x.id === id ? { ...x, ...patch } : x)) }));
      try {
        const u = await fetch(`/api/file-drops/${token}/upload-url`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot: slot.slot, fileName: file.name, fileType: file.type, fileSize: file.size }) });
        const uj = await u.json();
        if (!u.ok) throw new Error(uj.error || 'Could not start the upload');
        await put(uj.uploadUrl, file, pct => update({ pct }));
        const c = await fetch(`/api/file-drops/${token}/complete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slot: slot.slot, key: uj.key, fileName: file.name }) });
        const cj = await c.json();
        if (!c.ok) throw new Error(cj.error || 'Could not save that file');
        update({ pct: 100, done: cj.message || 'Saved.' });
      } catch (e: any) {
        update({ error: e.message || 'Something went wrong' });
      }
    }
    load();
  };

  const page: React.CSSProperties = { minHeight: '100vh', background: 'var(--hj-paper, #fffbf0)', padding: '24px 16px 64px' };
  const wrap: React.CSSProperties = { maxWidth: 680, margin: '0 auto' };
  if (err) return <div style={page}><div style={wrap}><div style={{ background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 16, padding: 24 }}><p className="m-0" style={{ fontSize: 18, fontWeight: 700 }}>{err}</p><p style={{ marginTop: 8, color: 'var(--hj-beige-ink-2)' }}>Ask whoever sent it for a new link.</p></div></div></div>;
  if (!drop) return <div style={{ ...page, display: 'flex', justifyContent: 'center', paddingTop: 120 }}><Loader2 className="w-7 h-7 animate-spin" /></div>;

  return (
    <div style={page}>
      <div style={wrap}>
        <div style={{ background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 16, padding: '22px 22px 20px' }}>
          <p className="m-0" style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--hj-beige-ink-2)' }}>Add files to Hey Joe</p>
          <h1 className="m-0" style={{ fontSize: 'clamp(22px, 5vw, 30px)', fontWeight: 800, lineHeight: 1.15, marginTop: 6, overflowWrap: 'anywhere' }}>{drop.title}</h1>
          {drop.note && <p style={{ marginTop: 10, fontSize: 15, lineHeight: 1.5 }}>{drop.note}</p>}
          <p style={{ marginTop: 10, fontSize: 13.5, color: 'var(--hj-beige-ink-2)' }}>
            {drop.expired ? 'This link has expired.' : `Each image is saved where it belongs as soon as it uploads. This link works until ${new Date(drop.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}.`}
          </p>
        </div>

        {!drop.expired && drop.slots.map(slot => {
          const rows = progress[slot.slot] || [];
          const kept = drop.files.filter(f => f.slot === slot.slot);
          return (
            <SlotCard key={slot.slot} slot={slot} hint={HINT[slot.slot]} rows={rows} kept={kept} onFiles={fs => send(slot, fs)} />
          );
        })}
      </div>
    </div>
  );
};

const SlotCard: React.FC<{ slot: Slot; hint?: string; rows: Progress[]; kept: DropFile[]; onFiles: (f: FileList) => void }> = ({ slot, hint, rows, kept, onFiles }) => {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const shown = slot.accept !== 'any' ? slot.current : null; // an image, or a video's poster frame
  return (
    <div style={{ marginTop: 16, background: '#fff', border: '1px solid var(--hj-beige-line)', borderRadius: 16, padding: 18 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {shown && <img src={shown} alt="" style={{ width: slot.slot === 'store_avatar' || slot.slot === 'host_photo' ? 72 : 128, height: 72, objectFit: 'cover', borderRadius: slot.slot === 'store_avatar' ? 999 : 10, flex: 'none' }} />}
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <p className="m-0" style={{ fontSize: 17, fontWeight: 800, color: 'var(--hj-ink)' }}>{slot.label}</p>
          {hint && <p className="m-0" style={{ marginTop: 4, fontSize: 14, color: 'var(--hj-ink)', lineHeight: 1.45 }}>{hint}</p>}
        </div>
      </div>
      <button
        type="button"
        onClick={() => input.current?.click()}
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files); }}
        style={{ marginTop: 14, width: '100%', minHeight: 96, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, border: `2px dashed ${over ? 'var(--hj-ink)' : 'var(--hj-beige-line)'}`, background: over ? 'var(--hj-beige)' : 'var(--hj-beige-hover)', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--hj-ink)', fontSize: 15, fontWeight: 700, padding: 12 }}
      >
        <Upload className="w-5 h-5" />
        {shown ? 'Replace it: drop a file or tap to choose' : `Drop ${slot.multiple ? 'files' : 'a file'} here or tap to choose`}
      </button>
      <input ref={input} type="file" hidden multiple={slot.multiple} accept={slot.accept === 'image' ? 'image/*' : slot.accept === 'video' ? 'video/mp4,video/quicktime,video/webm' : undefined} onChange={e => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }} />
      {[...rows].reverse().map(r => (
        <p key={r.id} className="m-0" style={{ marginTop: 10, fontSize: 14, display: 'flex', gap: 8, alignItems: 'center', color: r.error ? '#b42318' : 'var(--hj-ink)', overflowWrap: 'anywhere' }}>
          {r.error ? null : r.done ? <Check className="w-4 h-4 flex-none" /> : <Loader2 className="w-4 h-4 animate-spin flex-none" />}
          <span><strong>{r.name}</strong> · {r.error || r.done || `${r.pct}%`}</span>
        </p>
      ))}
      {slot.multiple && kept.length > 0 && (
        <p className="m-0" style={{ marginTop: 10, fontSize: 13.5, color: 'var(--hj-ink)' }}>{kept.length} file{kept.length === 1 ? '' : 's'} on this link: {kept.map(f => f.name).join(', ')}</p>
      )}
    </div>
  );
};

export default DropPage;
